import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@organizacaox/lai-design-system";
import { AudioLines, Check, ListChecks, Pause, Phone, Play, Sparkles, Users } from "lucide-react";

const scenes = ["calls", "leads", "insights"] as const;
const icons = [Phone, Users, Sparkles];

/** Apresentação ilustrativa do produto; não representa dados de uma conta. */
export function AuthShowcase() {
  const { t } = useTranslation();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!api) return;
    const select = () => setCurrent(api.selectedScrollSnap());
    api.on("select", select);
    return () => {
      api.off("select", select);
    };
  }, [api]);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const desktop = window.matchMedia("(min-width: 1024px)");
    if (!api || paused || hovered || focused) return;
    const timer = window.setInterval(() => {
      if (!reduced.matches && desktop.matches && !document.hidden) api.scrollNext();
    }, 7000);
    return () => window.clearInterval(timer);
  }, [api, paused, hovered, focused]);

  return (
    <aside
      className="relative hidden min-w-0 flex-1 flex-col items-center justify-center gap-8 overflow-hidden bg-primary p-10 text-primary-foreground lg:flex xl:p-12"
      aria-label={t("auth.showcase.label")}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 -top-24 size-[28rem] rounded-full bg-white/10 blur-3xl"
      />
      <Carousel
        setApi={setApi}
        opts={{ loop: true }}
        className="relative w-full max-w-2xl"
        aria-label={t("auth.showcase.label")}
        aria-live="off"
      >
        <CarouselContent>
          {scenes.map((scene, index) => {
            const Icon = icons[index]!;
            return (
              <CarouselItem key={scene} aria-hidden={current !== index}>
                <div className="flex flex-col items-center gap-8">
                  <div
                    aria-hidden="true"
                    className="w-full rounded-2xl bg-background p-6 text-foreground shadow-2xl shadow-black/20 xl:p-10"
                  >
                    <div className="mb-8 flex items-center justify-between border-b pb-5">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <img src="/logo.svg" alt="" className="size-8" />
                        Disk
                      </div>
                      <Icon className="size-5 text-primary" />
                    </div>
                    <div className="flex min-h-48 flex-col justify-center gap-5">
                      <div className="flex items-center gap-4">
                        <div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                          <Icon className="size-7" />
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm font-medium">
                            {t(`auth.showcase.${scene}.preview`)}
                          </p>
                          <div className="h-2 w-24 rounded-full bg-muted" />
                        </div>
                      </div>
                      {scene === "calls" ? (
                        <div className="flex h-20 items-center justify-center gap-1.5 rounded-xl bg-primary/5">
                          {Array.from({ length: 29 }, (_, i) => (
                            <span
                              key={i}
                              className="w-1.5 rounded-full bg-primary/60"
                              style={{ height: `${12 + ((i * 17) % 47)}px` }}
                            />
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {[0, 1, 2].map((row) => (
                            <div
                              key={row}
                              className="flex items-center gap-3 rounded-xl border p-3"
                            >
                              {scene === "leads" ? (
                                <ListChecks className="size-4 text-primary" />
                              ) : (
                                <Check className="size-4 text-primary" />
                              )}
                              <div
                                className="h-2 rounded-full bg-muted"
                                style={{ width: `${65 - row * 13}%` }}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <AudioLines className="size-4 text-primary" />
                        {t("common.productTagline")}
                      </div>
                    </div>
                  </div>
                  <div className="max-w-md text-center">
                    <h2 className="text-2xl font-semibold tracking-tight">
                      {t(`auth.showcase.${scene}.title`)}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-primary-foreground/80">
                      {t(`auth.showcase.${scene}.description`)}
                    </p>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>
      <div className="flex items-center gap-1">
        {scenes.map((scene, index) => (
          <Button
            key={scene}
            type="button"
            variant="ghost"
            size="icon"
            className="text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
            onClick={() => api?.scrollTo(index)}
            aria-label={t(`auth.showcase.${scene}.title`)}
            aria-current={current === index ? "true" : undefined}
          >
            <span
              className={`h-1.5 rounded-full transition-all motion-reduce:transition-none ${current === index ? "w-7 bg-current" : "w-1.5 bg-current opacity-40"}`}
            />
          </Button>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-2 text-primary-foreground hover:bg-white/10 hover:text-primary-foreground"
          onClick={() => setPaused(!paused)}
          aria-label={t(paused ? "auth.showcase.resume" : "auth.showcase.pause")}
        >
          {paused ? <Play className="size-4" /> : <Pause className="size-4" />}
        </Button>
      </div>
    </aside>
  );
}
